/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { PillarsSection } from './components/PillarsSection';
import { CustomErpSection } from './components/CustomErpSection';
import { SeoSection } from './components/SeoSection';
import { ResultsAndProcess } from './components/ResultsAndProcess';
import { IndustriesSection } from './components/IndustriesSection';
import { VentureLabSection } from './components/VentureLabSection';
import { CaseStudiesSection } from './components/CaseStudiesSection';
import { HostingPricingSection } from './components/HostingPricingSection';
import { TestimonialsSection } from './components/TestimonialsSection';
import { BookingCtaSection } from './components/BookingCtaSection';
import { Footer } from './components/Footer';
import { PartnersSection } from './components/PartnersSection';
import { PriceCatalogueSection } from './components/PriceCatalogueSection';
import { PageFaqSection } from './components/PageFaqSection';
import { LazySection } from './components/LazySection';
import { RegionalHubView } from './components/RegionalHubView';
import { CaseStudy, HostingPlan, HOSTING_PLANS, SITE_CONTACT } from './data/siteData';
import { MessageSquare } from 'lucide-react';
import { getViewFromPath, pushPermalink, AppView, ROUTE_MAP } from './utils/router';
import { initializeGoogleAnalytics, trackPageView } from './utils/analytics';

// =========================================================================
// Code-Splitting: Lazy-load non-home views & heavy modals
// Drastically eliminates unused JavaScript from initial bundle (Lighthouse target)
// =========================================================================
const SolutionsView = lazy(() => import('./components/DedicatedViews').then((m) => ({ default: m.SolutionsView })));
const AppDevelopmentView = lazy(() => import('./components/DedicatedViews').then((m) => ({ default: m.AppDevelopmentView })));
const WebsiteDevelopmentView = lazy(() => import('./components/DedicatedViews').then((m) => ({ default: m.WebsiteDevelopmentView })));
const CustomErpView = lazy(() => import('./components/DedicatedViews').then((m) => ({ default: m.CustomErpView })));
const AiAutomationView = lazy(() => import('./components/DedicatedViews').then((m) => ({ default: m.AiAutomationView })));
const SeoGrowthView = lazy(() => import('./components/DedicatedViews').then((m) => ({ default: m.SeoGrowthView })));
const CybersecurityView = lazy(() => import('./components/DedicatedViews').then((m) => ({ default: m.CybersecurityView })));
const CloudHostingDedicatedView = lazy(() => import('./components/DedicatedViews').then((m) => ({ default: m.CloudHostingDedicatedView })));
const PricingView = lazy(() => import('./components/DedicatedViews').then((m) => ({ default: m.PricingView })));
const ContactView = lazy(() => import('./components/DedicatedViews').then((m) => ({ default: m.ContactView })));

const InsightsView = lazy(() => import('./components/InsightsView').then((m) => ({ default: m.InsightsView })));
const StoreView = lazy(() => import('./components/StoreView').then((m) => ({ default: m.StoreView })));
const DomainChecker = lazy(() => import('./components/DomainChecker').then((m) => ({ default: m.DomainChecker })));
const AdminPortalView = lazy(() => import('./components/AdminPortalView').then((m) => ({ default: m.AdminPortalView })));

// Modals lazy-loaded on demand
const StrategySessionModal = lazy(() => import('./components/StrategySessionModal').then((m) => ({ default: m.StrategySessionModal })));
const CaseStudyModal = lazy(() => import('./components/CaseStudyModal').then((m) => ({ default: m.CaseStudyModal })));
const HostingOrderModal = lazy(() => import('./components/HostingOrderModal').then((m) => ({ default: m.HostingOrderModal })));
const ClientPortalModal = lazy(() => import('./components/ClientPortalModal').then((m) => ({ default: m.ClientPortalModal })));
const PaybillModal = lazy(() => import('./components/PaybillModal').then((m) => ({ default: m.PaybillModal })));

const ViewLoadingFallback = () => (
  <div className="py-28 text-center flex flex-col items-center justify-center min-h-[50vh]" role="status" aria-label="Loading page">
    <div className="h-8 w-8 rounded-full border-2 border-slate-200 border-t-[#b88e4c] animate-spin mb-3" />
    <span className="text-xs font-mono text-slate-500">Loading experience...</span>
  </div>
);

export default function App() {
  // Initialize current view directly from browser URL permalink
  const [currentView, setCurrentView] = useState<AppView>(() => {
    if (typeof window === 'undefined') return 'home';

    // 1. Check path first (e.g. /website-development or /portal)
    const pathView = getViewFromPath(window.location.pathname);
    if (pathView !== 'home') return pathView;

    // 2. Check hash fallback (e.g. #/website-development, #portal, #admin)
    if (window.location.hash) {
      if (
        window.location.hash === '#publisher' ||
        window.location.hash === '#engine' ||
        window.location.hash === '#portal' ||
        window.location.hash === '#admin' ||
        window.location.hash === '#editorial-portal'
      ) {
        return 'portal';
      }
      const cleanHash = window.location.hash.replace(/^#\/?/, '/');
      const hashView = getViewFromPath(cleanHash);
      if (hashView !== 'home') return hashView;
    }

    // 3. Check query param fallback (e.g. ?page=website-development or ?view=pricing)
    const params = new URLSearchParams(window.location.search);
    const queryParam = params.get('page') || params.get('view');
    if (queryParam) {
      const queryView = getViewFromPath('/' + queryParam);
      if (queryView !== 'home') return queryView;
    }

    return 'home';
  });

  const [selectedPillarTarget, setSelectedPillarTarget] = useState<string>('infrastructure');

  // Modals state
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingService, setBookingService] = useState('Website Development');
  const [portalOpen, setPortalOpen] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [selectedPlanForOrder, setSelectedPlanForOrder] = useState<HostingPlan | null>(HOSTING_PLANS[2]);
  const [orderDomainName, setOrderDomainName] = useState('mycompany');
  const [orderDomainTld, setOrderDomainTld] = useState('.co.ke');
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<CaseStudy | null>(null);

  // Direct Lipa na M-Pesa Paybill modal state
  const [paybillOpen, setPaybillOpen] = useState(false);
  const [paybillAmount, setPaybillAmount] = useState<number | undefined>(undefined);
  const [paybillTitle, setPaybillTitle] = useState('Hosting & Cloud Infrastructure');

  // Sync permalinks on mount and setup popstate/hash listeners
  useEffect(() => {
    // Initial sync of document title, canonical link & permalink
    pushPermalink(currentView);

    const handlePopState = () => {
      const resolved = getViewFromPath(window.location.pathname);
      setCurrentView(resolved);
    };

    const handleHashChange = () => {
      if (
        window.location.hash === '#publisher' ||
        window.location.hash === '#engine' ||
        window.location.hash === '#portal' ||
        window.location.hash === '#admin' ||
        window.location.hash === '#editorial-portal'
      ) {
        setCurrentView('portal');
        pushPermalink('portal');
      } else if (window.location.hash) {
        const clean = window.location.hash.replace(/^#\/?/, '/');
        const resolved = getViewFromPath(clean);
        if (resolved !== 'home') {
          setCurrentView(resolved);
          pushPermalink(resolved);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handleHashChange);

    // Initialize Google Analytics 4 script tag in document head
    initializeGoogleAnalytics();

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Track actual page view in GA4 & Local Analytics on view transition
  useEffect(() => {
    trackPageView(currentView, window.location.pathname);
  }, [currentView]);

  const handleNavigate = (view: string, targetId?: string) => {
    const appView = (view as AppView) || 'home';
    setCurrentView(appView);
    pushPermalink(appView);

    if (targetId) {
      setSelectedPillarTarget(targetId);
      setTimeout(() => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }, 50);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenBooking = (service?: string) => {
    if (service) setBookingService(service);
    setBookingOpen(true);
  };

  const handleOpenPaybill = (amount?: number, title?: string) => {
    setPaybillAmount(amount);
    if (title) setPaybillTitle(title);
    setPaybillOpen(true);
  };

  const handleOrderPlan = (plan: HostingPlan) => {
    setSelectedPlanForOrder(plan);
    setOrderModalOpen(true);
  };

  const handleOrderPlanById = (planId?: string) => {
    const plan = HOSTING_PLANS.find((p) => p.id === planId) || HOSTING_PLANS[2];
    setSelectedPlanForOrder(plan);
    setOrderModalOpen(true);
  };

  const handleOrderDomain = (domain: string, tld: string) => {
    setOrderDomainName(domain);
    setOrderDomainTld(tld);
    setSelectedPlanForOrder(HOSTING_PLANS[1]);
    setOrderModalOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900 font-sans selection:bg-[#b88e4c] selection:text-white">
      {/* Top Header */}
      {currentView !== 'portal' && (
        <Header
          currentView={currentView}
          onNavigate={handleNavigate}
          onOpenBooking={() => handleOpenBooking('Strategy Session')}
          onOpenPortal={() => {
            window.open(SITE_CONTACT.portalUrl, '_blank', 'noreferrer');
          }}
          onOpenPaybill={() => handleOpenPaybill(3875, 'LiteSpeed Hosting & Domain')}
        />
      )}

      {/* Main View Router */}
      <main className="flex-1">
        {currentView === 'home' && (
          <>
            <Hero
              onOpenBooking={() => handleOpenBooking('Digital Infrastructure')}
              onOpenPaybill={() => handleOpenPaybill(3875, 'LiteSpeed Hosting Plan')}
              onViewPricing={() => {
                handleNavigate('pricing');
              }}
              onExploreWork={() => {
                handleNavigate('website-development');
              }}
            />

            {/* Core Growth Stack */}
            <PillarsSection
              onSelectSolution={(pillarId, itemName) => {
                if (itemName) {
                  const lower = itemName.toLowerCase();
                  if (lower.includes('website') || lower.includes('web platform') || lower.includes('commerce')) {
                    handleNavigate('website-development');
                  } else if (lower.includes('app') || lower.includes('mobile') || lower.includes('software')) {
                    handleNavigate('app-development');
                  } else if (lower.includes('erp') || lower.includes('excel') || lower.includes('wholesale')) {
                    handleNavigate('custom-erps');
                  } else if (lower.includes('ai') || lower.includes('decision') || lower.includes('automation') || lower.includes('rpa')) {
                    handleNavigate('ai-solutions');
                  } else if (lower.includes('seo') || lower.includes('search')) {
                    handleNavigate('seo-growth');
                  } else if (lower.includes('cloud') || lower.includes('hosting')) {
                    handleNavigate('hosting');
                  } else if (lower.includes('security') || lower.includes('cyber')) {
                    handleNavigate('cybersecurity');
                  } else {
                    handleNavigate('solutions');
                  }
                } else {
                  if (pillarId === 'infrastructure') handleNavigate('website-development');
                  else if (pillarId === 'intelligence') handleNavigate('ai-solutions');
                  else if (pillarId === 'growth') handleNavigate('seo-growth');
                  else handleNavigate('solutions');
                }
              }}
              onOpenBooking={() => handleOpenBooking('Pillars Consultation')}
            />

            {/* High-Intent Feature: Convert Work Excels to Custom ERP Apps */}
            <div className="cv-auto">
              <CustomErpSection
                onOpenBooking={(service) => handleOpenBooking(service || 'Custom ERP & Excel Conversion')}
              />
            </div>

            {/* Official 2026 Transparent Price Catalogue */}
            <LazySection placeholderHeight="650px">
              <PriceCatalogueSection
                onOpenBooking={(pkg) => handleOpenBooking(pkg || 'Service Package')}
                onOpenPaybill={(amount, title) => handleOpenPaybill(amount, title)}
              />
            </LazySection>

            {/* SEO Engine & Local Search */}
            <LazySection placeholderHeight="500px">
              <SeoSection
                onOpenBooking={() => handleOpenBooking('SEO Audit & Strategy')}
                onExploreSeo={() => {
                  setSelectedPillarTarget('growth');
                  handleNavigate('solutions');
                }}
              />
            </LazySection>

            {/* Measurable Outcomes & 6-Stage Process */}
            <LazySection placeholderHeight="550px">
              <ResultsAndProcess
                onOpenBooking={() => handleOpenBooking('Operating System Diagnostic')}
              />
            </LazySection>

            {/* 10 Industry Verticals */}
            <LazySection placeholderHeight="500px">
              <IndustriesSection
                onOpenBooking={(industry) => handleOpenBooking(`${industry} Infrastructure`)}
                onExploreWork={() => handleNavigate('website-development')}
              />
            </LazySection>

            {/* Selected Work Portfolio (All 19 Case Studies Deployed in Kenya) */}
            <LazySection placeholderHeight="600px">
              <CaseStudiesSection
                onSelectCaseStudy={(cs) => setSelectedCaseStudy(cs)}
                onOpenBooking={() => handleOpenBooking('Case Study Architecture')}
                onViewAllWebsites={() => handleNavigate('website-development')}
              />
            </LazySection>

            {/* NVMe LiteSpeed Cloud Hosting Packages */}
            <LazySection placeholderHeight="500px">
              <HostingPricingSection
                onOrderPlan={handleOrderPlan}
                onOpenBooking={() => handleOpenBooking('Hosting & Cloud Setup')}
              />
            </LazySection>

            {/* Google Business Profile Verified Reviews (Place ID: ChIJr9Av3PYrBmIR4Tfmygy5Hfw) */}
            <LazySection placeholderHeight="450px">
              <TestimonialsSection
                onOpenBooking={() => handleOpenBooking('Client Strategy Session')}
              />
            </LazySection>

            {/* Ecosystem & Commercial Alliances (Partners Section) */}
            <LazySection placeholderHeight="350px">
              <PartnersSection
                onOpenBooking={() => handleOpenBooking('Strategic Alliance')}
              />
            </LazySection>

            {/* Strategic Diagnostic Callout */}
            <LazySection placeholderHeight="400px">
              <BookingCtaSection
                onOpenBooking={() => handleOpenBooking('Executive Strategy Session')}
              />
            </LazySection>
          </>
        )}

        <Suspense fallback={<ViewLoadingFallback />}>
          {/* Turnkey Hardware, POS & Solar Store */}
          {currentView === 'store' && (
            <StoreView
              onOpenPaybill={(amount, title) => handleOpenPaybill(amount, title)}
              onOpenBooking={handleOpenBooking}
            />
          )}

          {/* Dedicated Separate Solution: Custom ERPs */}
          {currentView === 'custom-erps' && (
            <CustomErpView
              onOpenBooking={handleOpenBooking}
              onOpenOrder={handleOrderPlanById}
              onNavigateHome={() => handleNavigate('home')}
              onNavigateView={handleNavigate}
            />
          )}

          {/* Dedicated Separate Solution: App Development */}
          {currentView === 'app-development' && (
            <AppDevelopmentView
              onOpenBooking={handleOpenBooking}
              onOpenOrder={handleOrderPlanById}
              onNavigateHome={() => handleNavigate('home')}
              onNavigateView={handleNavigate}
            />
          )}

          {/* Dedicated Separate Solution: Website Development */}
          {currentView === 'website-development' && (
            <WebsiteDevelopmentView
              onOpenBooking={handleOpenBooking}
              onOpenOrder={handleOrderPlanById}
              onNavigateHome={() => handleNavigate('home')}
              onNavigateView={handleNavigate}
              onSelectCaseStudy={(study) => setSelectedCaseStudy(study)}
            />
          )}

          {/* Dedicated Separate Solution: AI Solutions & Automation */}
          {currentView === 'ai-solutions' && (
            <AiAutomationView
              onOpenBooking={handleOpenBooking}
              onOpenOrder={handleOrderPlanById}
              onNavigateHome={() => handleNavigate('home')}
              onNavigateView={handleNavigate}
            />
          )}

          {/* Dedicated Separate Solution: SEO & Search Growth */}
          {currentView === 'seo-growth' && (
            <SeoGrowthView
              onOpenBooking={handleOpenBooking}
              onOpenOrder={handleOrderPlanById}
              onNavigateHome={() => handleNavigate('home')}
              onNavigateView={handleNavigate}
            />
          )}

          {/* Solutions Directory View */}
          {currentView === 'solutions' && (
            <SolutionsView
              initialPillar={selectedPillarTarget}
              onOpenBooking={handleOpenBooking}
              onOpenOrder={(planId) => {
                const plan = HOSTING_PLANS.find((p) => p.id === planId) || HOSTING_PLANS[2];
                handleOrderPlan(plan);
              }}
              onNavigateHome={() => handleNavigate('home')}
              onNavigateView={handleNavigate}
            />
          )}

          {/* Dedicated Separate Solution: Cybersecurity & Compliance */}
          {currentView === 'cybersecurity' && (
            <CybersecurityView
              onOpenBooking={handleOpenBooking}
              onOpenOrder={handleOrderPlanById}
              onNavigateHome={() => handleNavigate('home')}
              onNavigateView={handleNavigate}
            />
          )}

          {/* Dedicated Separate Solution: Cloud & NVMe LiteSpeed Hosting */}
          {currentView === 'hosting' && (
            <CloudHostingDedicatedView
              onOpenBooking={handleOpenBooking}
              onOpenOrder={handleOrderPlanById}
              onNavigateHome={() => handleNavigate('home')}
              onNavigateView={handleNavigate}
            />
          )}

          {currentView === 'domains' && (
            <>
              <DomainChecker
                onViewHostingPlans={() => handleNavigate('hosting')}
                onViewAllPricing={() => handleNavigate('pricing')}
                onOrderDomain={handleOrderDomain}
              />
              <HostingPricingSection
                asPageHeader={false}
                onOrderPlan={handleOrderPlan}
                onOpenBooking={() => handleOpenBooking('Domain & DNS Setup')}
              />
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <PageFaqSection
                  categoryName="Domain Registration & KeNIC Registry"
                  faqs={ROUTE_MAP['domains'].faqs || []}
                  onOpenBooking={() => handleOpenBooking('Domain & DNS Setup')}
                />
              </div>
            </>
          )}

          {currentView === 'pricing' && (
            <>
              <PricingView
                onOpenBooking={handleOpenBooking}
                onOpenOrder={(planId) => {
                  const plan = HOSTING_PLANS.find((p) => p.id === planId) || HOSTING_PLANS[2];
                  handleOrderPlan(plan);
                }}
                onNavigateHome={() => handleNavigate('home')}
              />
              <BookingCtaSection onOpenBooking={() => handleOpenBooking('Custom Enterprise Plan')} />
            </>
          )}

          {currentView === 'industries' && (
            <>
              <IndustriesSection
                isPageHeader={true}
                onOpenBooking={(ind) => handleOpenBooking(`${ind} Industry Stack`)}
                onExploreWork={() => handleNavigate('website-development')}
              />
              <CaseStudiesSection
                onSelectCaseStudy={(cs) => setSelectedCaseStudy(cs)}
                onOpenBooking={() => handleOpenBooking('Industry Architecture')}
                onViewAllWebsites={() => handleNavigate('website-development')}
              />
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <PageFaqSection
                  categoryName="Industry Digital Stacks"
                  faqs={ROUTE_MAP['industries'].faqs || []}
                  onOpenBooking={() => handleOpenBooking('Industry Architecture')}
                />
              </div>
              <BookingCtaSection onOpenBooking={() => handleOpenBooking('Industry Strategy Session')} />
            </>
          )}

          {currentView === 'case-studies' && (
            <>
              <CaseStudiesSection
                isPageHeader={true}
                onSelectCaseStudy={(cs) => setSelectedCaseStudy(cs)}
                onOpenBooking={() => handleOpenBooking('Case Studies Project')}
                onViewAllWebsites={() => handleNavigate('website-development')}
              />
              <TestimonialsSection
                onOpenBooking={() => handleOpenBooking('Project Architecture')}
              />
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <PageFaqSection
                  categoryName="Client Case Studies & Live Deployments"
                  faqs={ROUTE_MAP['case-studies'].faqs || []}
                  onOpenBooking={() => handleOpenBooking('Case Studies Project')}
                />
              </div>
              <BookingCtaSection onOpenBooking={() => handleOpenBooking('Project Consultation')} />
            </>
          )}

          {currentView === 'insights' && (
            <>
              <InsightsView
                onOpenBooking={handleOpenBooking}
                onNavigateHome={() => handleNavigate('home')}
              />
            </>
          )}

          {currentView === 'venture-lab' && (
            <>
              <VentureLabSection
                isPageHeader={true}
                onOpenBooking={() => handleOpenBooking('Venture Lab Seed Pitch')}
                onExploreVentureLab={() => handleNavigate('solutions')}
              />
              <CaseStudiesSection
                onSelectCaseStudy={(cs) => setSelectedCaseStudy(cs)}
                onOpenBooking={() => handleOpenBooking('Startup Build')}
                onViewAllWebsites={() => handleNavigate('website-development')}
              />
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <PageFaqSection
                  categoryName="Venture Lab & Startup Co-Founder Studio"
                  faqs={ROUTE_MAP['venture-lab'].faqs || []}
                  onOpenBooking={() => handleOpenBooking('Co-Founder Partnership')}
                />
              </div>
              <BookingCtaSection onOpenBooking={() => handleOpenBooking('Co-Founder Partnership')} />
            </>
          )}

          {/* Localized Regional Hub Views (Nairobi, Mombasa, Nakuru, Kisumu, Meru) */}
          {currentView === 'kenya-nairobi' && (
            <RegionalHubView
              hubKey="nairobi"
              onOpenBooking={handleOpenBooking}
              onOpenPaybill={handleOpenPaybill}
            />
          )}
          {currentView === 'kenya-mombasa' && (
            <RegionalHubView
              hubKey="mombasa"
              onOpenBooking={handleOpenBooking}
              onOpenPaybill={handleOpenPaybill}
            />
          )}
          {currentView === 'kenya-nakuru' && (
            <RegionalHubView
              hubKey="nakuru"
              onOpenBooking={handleOpenBooking}
              onOpenPaybill={handleOpenPaybill}
            />
          )}
          {currentView === 'kenya-kisumu' && (
            <RegionalHubView
              hubKey="kisumu"
              onOpenBooking={handleOpenBooking}
              onOpenPaybill={handleOpenPaybill}
            />
          )}
          {currentView === 'kenya-meru' && (
            <RegionalHubView
              hubKey="meru"
              onOpenBooking={handleOpenBooking}
              onOpenPaybill={handleOpenPaybill}
            />
          )}

          {currentView === 'contact' && (
            <ContactView
              onOpenBooking={handleOpenBooking}
              onOpenOrder={() => setOrderModalOpen(true)}
              onNavigateHome={() => handleNavigate('home')}
            />
          )}

          {currentView === 'portal' && (
            <AdminPortalView
              onNavigateHome={() => handleNavigate('home')}
              onNavigateView={(view) => handleNavigate(view)}
            />
          )}
        </Suspense>
      </main>

      {/* Footer */}
      {currentView !== 'portal' && (
        <div className="cv-auto">
          <Footer
            onNavigate={handleNavigate}
            onOpenPortal={() => {
              window.open(SITE_CONTACT.portalUrl, '_blank', 'noreferrer');
            }}
            onOpenBooking={() => handleOpenBooking('General Strategy')}
          />
        </div>
      )}

      {/* Floating Action Buttons: Paybill + WhatsApp */}
      {currentView !== 'portal' && (
        <div className="fixed bottom-6 right-6 z-30 flex flex-col items-end gap-2.5">
          {/* Direct Lipa na M-Pesa Paybill */}
          <button
            onClick={() => handleOpenPaybill(3875, 'Hosting & Cloud Services')}
            className="flex items-center gap-2 rounded-full bg-[#b88e4c] hover:bg-[#a37a36] text-white font-bold px-4 py-2.5 shadow-2xl transition-all duration-200 hover:scale-105 cursor-pointer font-mono text-xs border border-[#d4af37]/40 ring-1 ring-black/10 min-h-[44px]"
            title="Direct Lipa na M-Pesa Paybill: 542542, Account: 01550"
            aria-label="Direct Lipa na M-Pesa Paybill"
          >
            <span className="h-2 w-2 rounded-full bg-[#009fe3] animate-pulse" />
            <span>Paybill: <strong>542542</strong></span>
          </button>

          <a
            href={SITE_CONTACT.whatsappUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="Direct WhatsApp Chat"
            className="flex items-center gap-2 rounded-full bg-[#080d11] hover:bg-slate-900 text-white font-bold px-4 py-3 shadow-2xl transition-all duration-200 hover:scale-105 border border-slate-700 hover:border-[#009fe3] min-h-[44px]"
          >
            <MessageSquare className="h-5 w-5 text-[#009fe3]" />
            <span className="hidden sm:inline text-xs font-bold tracking-tight">
              Chat on WhatsApp
            </span>
          </a>
        </div>
      )}

      {/* Lazy Modals */}
      <Suspense fallback={null}>
        {paybillOpen && (
          <PaybillModal
            isOpen={paybillOpen}
            onClose={() => setPaybillOpen(false)}
            defaultAmount={paybillAmount}
            serviceTitle={paybillTitle}
          />
        )}

        {bookingOpen && (
          <StrategySessionModal
            isOpen={bookingOpen}
            onClose={() => setBookingOpen(false)}
            initialService={bookingService}
          />
        )}

        {selectedCaseStudy && (
          <CaseStudyModal
            caseStudy={selectedCaseStudy}
            onClose={() => setSelectedCaseStudy(null)}
            onOpenBooking={() => handleOpenBooking(selectedCaseStudy?.client)}
            onOpenPaybill={(amount, title) => handleOpenPaybill(amount, title)}
          />
        )}

        {orderModalOpen && (
          <HostingOrderModal
            isOpen={orderModalOpen}
            onClose={() => setOrderModalOpen(false)}
            plan={selectedPlanForOrder || undefined}
            selectedPlan={selectedPlanForOrder}
            initialDomain={orderDomainName}
            initialTld={orderDomainTld}
          />
        )}

        {portalOpen && (
          <ClientPortalModal
            isOpen={portalOpen}
            onClose={() => setPortalOpen(false)}
          />
        )}
      </Suspense>
    </div>
  );
}
